import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Select,
  Space,
  Typography,
  Spin,
  Upload,
  Tabs,
  Divider,
  ColorPicker,
  Switch
} from 'antd';
import {
  SaveOutlined,
  SettingOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  PictureOutlined,
  ShareAltOutlined,
  FileTextOutlined,
  UploadOutlined,
  BgColorsOutlined
} from '@ant-design/icons';
import { Helmet } from 'react-helmet';
import { useSiteSettings } from '../../hooks/useSiteSettings';

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const GOOGLE_FONTS = [
  'Tajawal',
  'Cairo',
  'Almarai',
  'Noto Kufi Arabic',
  'IBM Plex Arabic',
  'Noto Sans Arabic',
  'Amiri',
  'Lalezar',
  'Lemonada',
  'Changa',
  'El Messiri',
  'Reem Kufi',
  'Harmattan',
  'Mada',
  'Tajawal',
  'Rubik',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Playfair Display',
  'Merriweather',
  'Roboto',
  'Poppins',
  'Inter',
  'Nunito',
  'Work Sans',
  'Source Sans Pro',
  'Raleway',
  'Ubuntu',
  'Oswald'
];

const Settings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [faviconUrl, setFaviconUrl] = useState('/favicon.ico');
  const [colorState, setColorState] = useState({
    headerColor: '#ffffff',
    footerColor: '#0F1B2D',
    secondaryColor: '#C8953A'
  });
  const { refetch } = useSiteSettings();
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        message.error('يجب تسجيل الدخول أولاً');
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data.site_logo || '/logo.png');
        setFaviconUrl(data.site_favicon || '/favicon.ico');
        setColorState({
          headerColor: data.headerColor || '#ffffff',
          footerColor: data.footerColor || '#0F1B2D',
          secondaryColor: data.secondaryColor || '#C8953A'
        });
        form.setFieldsValue({
          site_name: data.site_name || '',
          siteDescription: data.siteDescription || '',
          site_keywords: data.site_keywords || '',
          site_font: data.site_font || 'Tajawal',
          site_language: data.site_language || 'ar',
          contactEmail: data.contactEmail || '',
          contactPhone: data.contactPhone || '',
          address: data.address || '',
          mapUrl: data.mapUrl || '',
          map_enabled: data.map_enabled === '1' || data.map_enabled === true,
          facebookUrl: data.facebookUrl || '',
          twitterUrl: data.twitterUrl || '',
          instagramUrl: data.instagramUrl || '',
          youtubeUrl: data.youtubeUrl || '',
          whatsappUrl: data.whatsappUrl || '',
          callUrl: data.callUrl || '',
          snapchatUrl: data.snapchatUrl || ''
        });
      }
    } catch (error) {
      console.error('خطأ في جلب الإعدادات:', error);
      message.error('تعذر جلب الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');

      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...values,
          map_enabled: values.map_enabled ? '1' : '0'
        })
      });

      if (res.ok) {
        message.success('تم حفظ الإعدادات بنجاح');
      } else {
        const errorData = await res.json();
        message.error(errorData.message || 'تعذر حفظ الإعدادات');
      }
    } catch (error) {
      console.error('خطأ في حفظ الإعدادات:', error);
      message.error('تعذر الاتصال بالخادم');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveColors = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(colorState)
      });
      if (res.ok) {
        await refetch();
        message.success('تم حفظ الألوان بنجاح');
      } else {
        message.error('تعذر حفظ الألوان');
      }
    } catch (error) {
      message.error('خطأ في الاتصال بالخادم');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (info) => {
    const file = info.file;
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('site_logo', file);

    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setLogoUrl(data.logoUrl);
        message.success('تم رفع الشعار بنجاح');
      } else {
        message.error('تعذر رفع الشعار');
      }
    } catch (error) {
      message.error('خطأ في رفع الشعار');
    } finally {
      setUploading(false);
    }
  };

  const handleFaviconUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('site_favicon', file);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings/favicon`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        setFaviconUrl(data.site_favicon);
        message.success('تم رفع الفاڤيكون بنجاح');
      } else {
        message.error('تعذر رفع الفاڤيكون');
      }
    } catch (error) {
      message.error('خطأ في رفع الفاڤيكون');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>جاري تحميل الإعدادات...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>إعدادات الموقع | لوحة التحكم</title>
      </Helmet>

      <div style={{ padding: '24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <div>
            <Title level={2} style={{ margin: 0, color: '#1A3651' }}>
              <SettingOutlined style={{ marginLeft: '8px' }} />
              إعدادات الموقع
            </Title>
            <Text type="secondary">
              إدارة جميع إعدادات الموقع من هنا — لا حاجة لتعديل الكود
            </Text>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => form.submit()}
              loading={saving}
              size="large"
            >
              حفظ الإعدادات
            </Button>
          </Space>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'basic',
                label: <span><GlobalOutlined /> الإعدادات الأساسية</span>,
                children: (
                  <Card style={{ marginBottom: '24px' }}>
                    <Row gutter={[24, 16]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="site_name"
                          label="اسم الموقع"
                          rules={[{ required: true, message: 'يرجى إدخال اسم الموقع' }]}
                        >
                          <Input placeholder="اسم الموقع الذي يظهر في الهيدر وعناوين الصفحات" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="siteDescription"
                          label="وصف الموقع"
                        >
                          <Input placeholder="وصف مختصر للموقع يظهر في Footer" />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Form.Item
                          name="site_keywords"
                          label="الكلمات المفتاحية (SEO)"
                          help="افصل بين الكلمات بفواصل، مثال: محاماة, استشارات قانونية, قضايا مدنية"
                        >
                          <Input.TextArea
                            rows={2}
                            placeholder="محاماة, استشارات قانونية, قضايا مدنية, قضايا جنائية"
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="site_font"
                          label="الخط الأساسي"
                        >
                          <Select
                            showSearch
                            placeholder="اختر خطًا"
                            optionFilterProp="children"
                          >
                            {GOOGLE_FONTS.map(font => (
                              <Option key={font} value={font}>
                                <span style={{ fontFamily: font }}>{font}</span>
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="site_language"
                          label="لغة الموقع"
                        >
                          <Select>
                            <Option value="ar">العربية (RTL)</Option>
                            <Option value="en">English (LTR)</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider />

                    <Text strong style={{ display: 'block', marginBottom: '12px' }}>
                      <PictureOutlined style={{ marginLeft: '8px' }} />
                      شعار الموقع
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                      <div style={{
                        width: 120,
                        height: 120,
                        border: '2px dashed #d9d9d9',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        background: '#fafafa'
                      }}>
                        <img
                          src={logoUrl}
                          alt="الشعار"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                          onError={(e) => { e.target.src = '/logo.png'; }}
                        />
                      </div>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        customRequest={({ file }) => handleLogoUpload({ file })}
                        disabled={uploading}
                      >
                        <Button
                          icon={<UploadOutlined />}
                          loading={uploading}
                          type="dashed"
                          size="large"
                        >
                          {uploading ? 'جاري الرفع...' : 'تغيير الشعار'}
                        </Button>
                      </Upload>
                    </div>

                    <Divider />

                    <Text strong style={{ display: 'block', marginBottom: '12px' }}>
                      <PictureOutlined style={{ marginLeft: '8px' }} />
                      فاڤيكون الموقع (أيقونة المتصفح)
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        border: '2px dashed #d9d9d9',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        background: '#fafafa'
                      }}>
                        <img
                          src={faviconUrl}
                          alt="الفاڤيكون"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e) => { e.target.src = '/favicon.ico'; }}
                        />
                      </div>
                      <Upload
                        accept="image/x-icon,image/png,image/svg+xml,image/*"
                        showUploadList={false}
                        customRequest={({ file }) => handleFaviconUpload(file)}
                      >
                        <Button icon={<UploadOutlined />} type="dashed" size="large">
                          تغيير الفاڤيكون
                        </Button>
                      </Upload>
                    </div>
                  </Card>
                )
              },
              {
                key: 'contact',
                label: <span><PhoneOutlined /> معلومات الاتصال</span>,
                children: (
                  <Card style={{ marginBottom: '24px' }}>
                    <Row gutter={[24, 16]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="contactEmail"
                          label="البريد الإلكتروني"
                          rules={[
                            { required: true, message: 'يرجى إدخال البريد الإلكتروني' },
                            { type: 'email', message: 'يرجى إدخال بريد صحيح' }
                          ]}
                        >
                          <Input prefix={<MailOutlined />} placeholder="info@example.com" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="contactPhone"
                          label="رقم الهاتف"
                        >
                          <Input prefix={<PhoneOutlined />} placeholder="7XXXXXXXX" />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          name="address"
                          label="العنوان"
                        >
                          <TextArea
                            placeholder="العنوان الكامل للمكتب"
                            rows={3}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={24}>
                        <Form.Item
                          name="map_enabled"
                          label="إظهار الموقع الجغرافي في صفحة اتصل بنا"
                          valuePropName="checked"
                          extra={
                            <Text type="secondary">
                              عند التفعيل وإدخال الرابط، تظهر خريطة تفاعلية صغيرة في صفحة "اتصل بنا" أسفل "يمكنك زيارتنا في مقرنا الرئيسي"، والنقر عليها ينقل الزائر إلى موقع المكتب على جوجل مابس.
                            </Text>
                          }
                        >
                          <Switch />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          name="mapUrl"
                          label="رابط الموقع الجغرافي (جوجل مابس)"
                          extra={
                            <Text type="secondary">
                              افتح جوجل مابس → ابحث عن مقر المكتب → زر مشاركة (Share) → انسخ الرابط والصقه هنا.
                            </Text>
                          }
                        >
                          <Input placeholder="https://maps.app.goo.gl/... أو https://www.google.com/maps/place/..." />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                )
              },
              {
                key: 'social',
                label: <span><ShareAltOutlined /> التواصل الاجتماعي</span>,
                children: (
                  <Card style={{ marginBottom: '24px' }}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                      اترك الحقل فارغًا إذا كنت لا تريد عرض أيقونة هذا الرابط في الموقع
                    </Text>
                    <Row gutter={[24, 16]}>
                      <Col xs={24} md={12}>
                        <Form.Item name="facebookUrl" label="رابط فيسبوك">
                          <Input placeholder="https://facebook.com/..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="twitterUrl" label="رابط تويتر / X">
                          <Input placeholder="https://x.com/..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="instagramUrl" label="رابط إنستغرام">
                          <Input placeholder="https://instagram.com/..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="youtubeUrl" label="رابط يوتيوب">
                          <Input placeholder="https://youtube.com/..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="tiktokUrl" label="رابط تيك توك">
                          <Input placeholder="https://www.tiktok.com/@username" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="whatsappUrl" label="رابط واتساب">
                          <Input placeholder="https://wa.me/967771678010" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="callUrl" label="رابط الاتصال">
                          <Input placeholder="tel:+967771678010" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item name="snapchatUrl" label="رابط سناب شات">
                          <Input placeholder="https://www.snapchat.com/add/username" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                )
              },
              {
                key: 'about',
                label: <span><FileTextOutlined /> صفحة "من نحن"</span>,
                children: <AboutTab />
              },
              {
                key: 'colors',
                label: <span><BgColorsOutlined /> إدارة الألوان</span>,
                children: (
                  <ColorsTab
                    colorState={colorState}
                    setColorState={setColorState}
                    saving={saving}
                    onSave={handleSaveColors}
                  />
                )
              }
            ]}
          />
        </Form>
      </div>
    </>
  );
};

/* ===== مكون فرعي لصفحة "من نحن" ===== */
const AboutTab = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/basic-info/about`);
      const data = await res.json();
      setImageUrl(data.image || '');
      form.setFieldsValue({
        title: data.title || '',
        content: data.content || '',
        vision: data.vision || '',
        mission: data.mission || '',
        strategic_goals: data.strategic_goals || '',
        values: data.values || '',
        work_fields: data.work_fields || '',
        org_structure: data.org_structure || ''
      });
    } catch (error) {
      console.error('خطأ في جلب بيانات من نحن:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/basic-info/about`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      });

      if (res.ok) {
        message.success('تم حفظ بيانات "من نحن" بنجاح');
      } else {
        message.error('تعذر الحفظ');
      }
    } catch (error) {
      message.error('خطأ في الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (info) => {
    const file = info.file;
    if (!file) return;

    const formData = new FormData();
    formData.append('about_image', file);

    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings/about-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setImageUrl(data.about_image);
        message.success('تم رفع الصورة بنجاح');
      }
    } catch (error) {
      message.error('خطأ في رفع الصورة');
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />;
  }

  return (
    <Card style={{ marginBottom: '24px' }}>
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Row gutter={[24, 16]}>
          <Col span={24}>
            <Form.Item name="title" label="عنوان الصفحة">
              <Input placeholder="من نحن" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="content" label="المحتوى الرئيسي (من نحن)">
              <TextArea rows={6} placeholder="وصف المكتب والخبرات والإنجازات..." />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="vision" label="الرؤية">
              <TextArea rows={4} placeholder="رؤية المكتب..." />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="mission" label="الرسالة">
              <TextArea rows={4} placeholder="رسالة المكتب..." />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="strategic_goals" label="الأهداف الاستراتيجية">
              <TextArea rows={6} placeholder="الأهداف الاستراتيجية للمكتب..." />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="values" label="القيم والمبادئ">
              <TextArea rows={4} placeholder="القيم التي يلتزم بها المكتب..." />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="work_fields" label="مجالات العمل">
              <TextArea rows={4} placeholder="مجالات الممارسة القانونية..." />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="org_structure" label="الهيكل التنظيمي">
              <TextArea rows={4} placeholder="هيكل المكتب التنظيمي..." />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Text strong style={{ display: 'block', marginBottom: '12px' }}>
          <PictureOutlined style={{ marginLeft: '8px' }} />
          صورة صفحة "من نحن"
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '24px' }}>
          {imageUrl && (
            <div style={{
              width: 200,
              height: 120,
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#fafafa'
            }}>
              <img src={imageUrl} alt="صورة من نحن" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={({ file }) => handleImageUpload({ file })}
          >
            <Button icon={<UploadOutlined />} type="dashed">
              {imageUrl ? 'تغيير الصورة' : 'رفع صورة'}
            </Button>
          </Upload>
        </div>

        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={saving}
            size="large"
          >
            حفظ بيانات "من نحن"
          </Button>
        </div>
      </Form>
    </Card>
  );
};

/* ===== مكوّن إدارة الألوان ===== */
const ColorsTab = ({ colorState, setColorState, saving, onSave }) => {
  const previewBar = (bg, label) => (
    <div style={{
      background: bg,
      height: 48,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      fontWeight: 600,
      color: 'rgba(0,0,0,0.6)'
    }}>
      {label}
    </div>
  );

  const colorRow = (key, label, defaultColor) => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '16px', marginBottom: '20px', flexWrap: 'wrap'
    }}>
      <div style={{ flex: 1, minWidth: '200px' }}>
        <Text strong style={{ display: 'block', marginBottom: '8px' }}>{label}</Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ColorPicker
            value={colorState[key]}
            onChangeComplete={(color) => setColorState(prev => ({ ...prev, [key]: color.toHexString() }))}
            disabledAlpha
            showText
          />
          <input
            type="color"
            value={colorState[key]}
            onChange={(e) => setColorState(prev => ({ ...prev, [key]: e.target.value }))}
            style={{ width: 44, height: 36, border: 'none', padding: 0, cursor: 'pointer', background: 'transparent' }}
          />
          <Text code>{colorState[key]}</Text>
        </div>
      </div>
    </div>
  );

  return (
    <Card style={{ marginBottom: '24px' }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: '20px' }}>
        اختر الألوان واسحب المنزلق ثم اضغط حفظ — تُطبّق مباشرة على الموقع بعد الحفظ.
        لون نص الرأس يتكيف تلقائياً ليبقى مقروءاً (فاتح أو غامق حسب الخلفية).
      </Text>

      {colorRow('headerColor', 'لون رأس الصفحة (الرأس)', '#ffffff')}
      {colorRow('footerColor', 'لون أسفل الصفحة (الفوتر)', '#0F1B2D')}
      {colorRow('secondaryColor', 'اللون التكميلي (التفاعل والأزرار)', '#C8953A')}

      <Divider />

      <Text strong style={{ display: 'block', marginBottom: '12px' }}>معاينة حية</Text>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        {previewBar(colorState.headerColor, 'رأس الصفحة — العنوان والقائمة')}
        <div style={{ background: 'var(--surface, #f4f4f4)', height: 60 }} />
        {previewBar(colorState.footerColor, 'أسفل الصفحة — الفوتر')}
      </div>

      <Divider />

      <div style={{ textAlign: 'center' }}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={onSave}
          loading={saving}
          size="large"
        >
          حفظ الألوان
        </Button>
      </div>
    </Card>
  );
};

export default Settings;
